import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, ShieldAlert, CheckCircle2, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import {
  SHARE_DETAIL_LEVELS,
  toShareDetailLevel,
  type ShareDetailLevel,
} from "@shared/trust";

const LEVEL_LABEL: Record<ShareDetailLevel, { en: string; ar: string }> = {
  headline: { en: "Headline only", ar: "العنوان فقط" },
  highlights: { en: "Headline + highlights", ar: "العنوان + أبرز الإنجازات" },
  full: { en: "Full receipt", ar: "الملخّص الكامل" },
};
const LEVEL_HINT: Record<ShareDetailLevel, { en: string; ar: string }> = {
  headline: {
    en: "Team sees only the one-line headline.",
    ar: "يرى الفريق العنوان المكوّن من سطر واحد فقط.",
  },
  highlights: {
    en: "Team sees the headline and your highlight bullets.",
    ar: "يرى الفريق العنوان ونقاط أبرز الإنجازات.",
  },
  full: {
    en: "Team sees the full receipt: highlights and blockers (blockers stay manager-only).",
    ar: "يرى الفريق الملخّص الكامل: أبرز الإنجازات والمعوقات (تبقى المعوقات للمدير فقط).",
  },
};

/**
 * Approve & Share preview — the trust gate (A3). Before any approval, this
 * renders the EXACT deterministic team-feed projection the server will publish,
 * fetched from `receipts.previewApproval` (the SAME snapshot/projection the
 * Team feed shows). It paraphrases NOTHING and computes NOTHING: the headline,
 * lines and blockers shown here come straight from `teamView`. Only after the
 * user sees this and confirms does the parent fire `receipts.approve`.
 */
export function ApprovePreviewSheet({
  receiptId,
  open,
  onOpenChange,
  onConfirm,
  busy,
  isPersonal = false,
}: {
  receiptId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Approve at the chosen per-receipt sharing level. */
  onConfirm: (level: ShareDetailLevel) => void;
  busy?: boolean;
  /**
   * The receipt's workspace is a PERSONAL workspace (isPersonal=true). When
   * set: no sharing-level selector, no "what your team will see" framing — the
   * preview is "your receipt" and approval finalizes it for the user only.
   * Approval still fires `onConfirm(level)` with the default level so
   * provenance/snapshot behaviour is identical; the level simply has no
   * audience. Sharing MECHANICS unchanged — copy + conditional rendering only.
   */
  isPersonal?: boolean;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  // Selected sharing level. null until the preview loads, then initialized from
  // the receipt's default scope so the selector reflects the user's default (or
  // the last-approved level). Changing it re-projects immediately.
  const [level, setLevel] = useState<ShareDetailLevel | null>(null);
  useEffect(() => {
    // Reset when the sheet opens for a (possibly different) receipt.
    if (open) setLevel(null);
  }, [open, receiptId]);

  const preview = trpc.receipts.previewApproval.useQuery(
    { receiptId: receiptId ?? 0, ...(level ? { shareDetailLevel: level } : {}) },
    { enabled: open && receiptId != null },
  );

  // Initialize the selector from the server's default scope once loaded.
  useEffect(() => {
    if (level == null && preview.data?.scope?.shareReceiptDetail) {
      setLevel(toShareDetailLevel(preview.data.scope.shareReceiptDetail as any));
    }
  }, [level, preview.data]);
  const effectiveLevel: ShareDetailLevel =
    level ?? toShareDetailLevel(preview.data?.scope?.shareReceiptDetail as any);

  const team = preview.data?.teamView ?? null;
  const manager = preview.data?.managerView ?? null;
  // Blockers a manager sees but the team does not (manager-only scope).
  const managerOnlyBlockers = manager
    ? manager.blockers.filter(b => !(team?.blockers ?? []).includes(b))
    : [];
  // Nothing shareable: receipt missing entirely, OR what every viewer would
  // see is effectively EMPTY (the user deleted the headline and every item).
  // An empty snapshot must never be published — the server enforces the same
  // rule (`empty_receipt`).
  const viewEmpty = (v: { headline: string; lines: string[]; shipped?: string[]; blockers: string[] } | null) =>
    v == null ||
    (!v.headline?.trim() &&
      (v.lines ?? []).length === 0 &&
      ((v as any).shipped ?? []).length === 0 &&
      (v.blockers ?? []).length === 0);
  const nothingToShare =
    preview.data !== undefined &&
    (preview.data.projection == null ||
      team == null ||
      (viewEmpty(team) && viewEmpty(manager)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="approve-preview"
        className="max-h-[90dvh] overflow-y-auto max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:pb-[calc(1.25rem+env(safe-area-inset-bottom))]"
      >
        <DialogHeader>
          <DialogTitle>
            {isPersonal
              ? isAr ? "ملخّصك" : "Your receipt"
              : isAr ? "هذا ما سيراه فريقك" : "This is what your team will see"}
          </DialogTitle>
          <DialogDescription>
            {isPersonal ? (
              isAr
                ? "الاعتماد يُثبّت هذا الملخّص لسجلّك الخاص ويُغذّي ملخّصك الأسبوعي. يبقى خاصاً بك."
                : "Approving finalizes this receipt for your own records and feeds your weekly receipt. It stays private to you."
            ) : isAr ? (
              "الاعتماد يشارك الملخّص أدناه تماماً، لا أكثر. يمكنك التعديل أولاً ثم إعادة فتح هذه المعاينة."
            ) : (
              <>
                Approving shares exactly the summary below, nothing more. You can
                edit first and re-open this preview.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Sharing level — TEAM workspaces only. A personal workspace has no
            team audience, so there is no sharing level to choose. */}
        {!isPersonal ? (
        <div className="mt-3" data-testid="share-level-selector">
          <div className="text-[11px] font-semibold tracking-wider text-muted-foreground mb-1.5">
            {isAr ? "مستوى المشاركة" : "SHARING LEVEL"}
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {SHARE_DETAIL_LEVELS.map(lv => {
              const active = effectiveLevel === lv;
              return (
                <button
                  key={lv}
                  type="button"
                  onClick={() => setLevel(lv)}
                  data-testid={`share-level-${lv}`}
                  aria-pressed={active}
                  className={cn(
                    "rounded-full border px-2 py-1.5 text-xs font-medium transition-colors",
                    active
                      ? "border-coral bg-coral-soft/60 text-coral"
                      : "border-border text-muted-foreground hover:bg-muted",
                  )}
                >
                  {LEVEL_LABEL[lv][isAr ? "ar" : "en"]}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            {LEVEL_HINT[effectiveLevel][isAr ? "ar" : "en"]}
          </p>
        </div>
        ) : null}

        {preview.isLoading ? (
          <div className="mt-4 space-y-2" data-testid="approve-preview-loading">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        ) : nothingToShare ? (
          <div className="mt-4 text-sm text-muted-foreground py-2" data-testid="approve-preview-empty">
            {isPersonal
              ? isAr
                ? "لا يوجد ما يُعتمَد بعد. أضف تحديثاً أولاً."
                : "There’s nothing to approve yet. Add an update first."
              : isAr
                ? "لا يوجد ما يُشارَك بعد. أضف تحديثاً قبل الاعتماد."
                : "There’s nothing to share yet. Add an update before approving."}
          </div>
        ) : team ? (
          <div className="mt-4 rounded-xl border border-border bg-card/60 p-4" data-testid="approve-preview-projection">
            <div className="flex items-center gap-1.5 text-xs text-coral mb-2">
              {isPersonal ? (
                <><Lock className="h-3.5 w-3.5" /> {isAr ? "إيصالك المعتمد · خاص بك" : "Your approved receipt · private to you"}</>
              ) : (
                <><Users className="h-3.5 w-3.5" /> {isAr ? "يُشارَك مع فريقك" : "Shared with your team"}</>
              )}
            </div>
            {team.headline ? (
              <div className="font-medium">{team.headline}</div>
            ) : null}
            {(team as any).shipped?.length ? (
              <div className="mt-2">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{isAr ? "المنجزات" : "Shipped"}</div>
                <ul className="mt-1 space-y-1 text-sm text-foreground/90 list-disc ps-5">
                  {(team as any).shipped.map((l: string, i: number) => (
                    <li key={i}>{l}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {team.lines.length ? (
              <div className="mt-2">
                {(team as any).shipped?.length ? (
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{isAr ? "أبرز الإنجازات" : "Highlights"}</div>
                ) : null}
                <ul className="mt-1 space-y-1 text-sm text-foreground/90 list-disc ps-5">
                  {team.lines.map((l, i) => (
                    <li key={i}>{l}</li>
                  ))}
                </ul>
              </div>
            ) : null}
            {team.blockers.length ? (
              <div className="mt-3">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  {isAr ? "المعوقات" : "Blockers"}
                </div>
                <ul className="mt-1 space-y-1 text-sm text-foreground/90 list-disc ps-5">
                  {team.blockers.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {!isPersonal && managerOnlyBlockers.length ? (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5">
                <div className="flex items-center gap-1.5 text-[11px] font-medium text-amber-800">
                  <ShieldAlert className="h-3.5 w-3.5" /> {isAr ? "للمدير فقط (لا يظهر للزملاء)" : "Manager-only (not shown to peers)"}
                </div>
                <ul className="mt-1 space-y-1 text-sm text-amber-900 list-disc ps-5">
                  {managerOnlyBlockers.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        <DialogFooter className="mt-6 gap-2 border-t border-border/60 pt-4 max-sm:flex-col-reverse">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-full max-sm:w-full"
          >
            {isAr ? "إلغاء" : "Cancel"}
          </Button>
          <Button
            onClick={() => onConfirm(effectiveLevel)}
            disabled={busy || preview.isLoading || nothingToShare}
            className="rounded-full max-sm:w-full"
          >
            {busy ? "…" : (
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" />{" "}
                {isPersonal
                  ? isAr ? "اعتماد" : "Approve"
                  : isAr ? "اعتماد ومشاركة" : <>Approve &amp; share</>}
              </span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
